import { ComponentMultiStyleConfig } from '@chakra-ui/react';
import { StyleFunctionProps } from '@chakra-ui/theme-tools';

import { AvatarProps } from './Avatar';

import { COLORS } from 'theme';

/*
 NOTE: All avatar group theme related items (excessLabel & avatarList) are implemented in /molecules/AvatarGroup
*/

export const avatarBoxStyles = {
  borderRadius: '25%',
  borderWidth: 0,
  borderColor: 'transparent',
  flexShrink: 0,
  overflow: 'hidden'
};

export const fontSizeMap = {
  '2xs': '8.4px',
  xs: '9.6px',
  sm: '12px',
  md: '14.4px',
  lg: '12.8px',
  xl: '19.2px',
  '2xl': '25.6px',
  '3xl': '38.4px',
  '4xl': '51.2px'
};

export const containerSizeStyleMap = {
  '2xs': {
    w: '14px',
    h: '14px',
    fontSize: '8.4px'
  },
  xs: {
    w: '16px',
    h: '16px',
    fontSize: '9.6px'
  },
  sm: {
    w: '20px',
    h: '20px',
    fontSize: '12px'
  },
  md: {
    w: '24px',
    h: '24px',
    fontSize: '14.4px'
  },
  lg: {
    w: '32px',
    h: '32px',
    fontSize: '12.8px'
  },
  xl: {
    w: '48px',
    h: '48px',
    fontSize: '19.2px'
  },
  '2xl': {
    w: '64px',
    h: '64px',
    fontSize: '25.6px'
  },
  '3xl': {
    w: '96px',
    h: '96px',
    fontSize: '38.4px'
  },
  '4xl': {
    w: '128px',
    h: '128px',
    fontSize: '51.2px'
  }
};

export const staggeredFirstIconStyleMap = {
  sm: {
    clipPath:
      'path("M3.5 0C1.567 0 0 1.567 0 3.5V10.5C0 12.433 1.567 14 3.5 14H5V9.5C5 7.01472 7.01472 5 9.5 5H14V3.5C14 1.567 12.433 0 10.5 0H3.5Z")',
    ...containerSizeStyleMap['2xs']
  },
  md: {
    clipPath:
      'path("M4 0C1.79086 0 0 1.79086 0 4V12C0 14.2091 1.79086 16 4 16H7V11.6667C7 9.08934 9.08934 7 11.6667 7H16V4C16 1.79086 14.2091 0 12 0H4Z")',
    ...containerSizeStyleMap.xs
  },
  lg: {
    clipPath:
      'path("M3.57143 0C1.59898 0 0 1.59898 0 3.57143V16.4286C0 18.401 1.59898 20 3.57143 20H10V17.2C10 13.2235 13.2235 10 17.2 10H20V3.57143C20 1.59898 18.401 0 16.4286 0H3.57143Z")',
    ...containerSizeStyleMap.sm
  }
};

export const staggeredLastIconStyleMap = {
  sm: {
    marginLeft: '6px',
    marginTop: '6px',
    ...containerSizeStyleMap['2xs']
  },
  md: {
    marginLeft: '8px',
    marginTop: '8px',
    ...containerSizeStyleMap.xs
  },
  lg: {
    marginLeft: '12px',
    marginTop: '12px',
    ...containerSizeStyleMap.sm
  }
};

const labelStyles = {
  fontSize: 'inherit',
  lineHeight: '75%',
  fontWeight: 'medium'
};

function getColorVariantStyles(baseColor: string) {
  return {
    container: {
      _light: {
        bg: `${baseColor}.dark`,
        '&[data-sillhouette="true"]': {
          bg: `${baseColor}.lightest`
        }
      },
      _dark: {
        bg: `${baseColor}.light`,
        '&[data-sillhouette="true"]': {
          bg: `${baseColor}.dark`
        }
      }
    },
    label: {
      _light: {
        color: `${baseColor}.lightest`,
        '&.chakra-icon': {
          color: `${baseColor}.dark`
        }
      },
      _dark: {
        color: `${baseColor}.darkest`,
        '&.chakra-icon': {
          color: `${baseColor}.lighter`
        }
      }
    }
  };
}

const AvatarTheme: ComponentMultiStyleConfig = {
  parts: ['container', 'label'],
  baseStyle: (props: AvatarProps & StyleFunctionProps) => ({
    container: {
      ...avatarBoxStyles,
      bg: props.src ? 'transparent' : props.bg,
      img: {
        borderRadius: 'inherit'
      },
      outline: 'none',
      _focusVisible: {
        transitionDuration: 'var(--chakra-transition-duration-normal)',
        boxShadow: `0px 0px 0px 1px ${COLORS.silver.lightest}, 0px 0px 0px 4px ${COLORS.blue.lighter}`,
        _dark: {
          boxShadow: `0px 0px 0px 1px ${COLORS.black.base}, 0px 0px 0px 4px ${COLORS.blue.lighter}`
        }
      }
    }
  }),
  variants: {
    mint: getColorVariantStyles('mint'),
    orange: getColorVariantStyles('orange'),
    teal: getColorVariantStyles('teal'),
    indigo: getColorVariantStyles('indigo'),
    purple: getColorVariantStyles('purple'),
    pink: getColorVariantStyles('pink'),
    internalLoadingVariant: {
      container: {
        _light: {
          bg: 'silver.base'
        },
        _dark: {
          bg: 'black.lightest'
        }
      },
      label: {
        _light: {
          color: 'silver.base'
        },
        _dark: {
          color: 'black.lightest'
        }
      }
    }
  },
  sizes: {
    '2xs': {
      container: containerSizeStyleMap['2xs'],
      label: labelStyles
    },
    xs: {
      container: containerSizeStyleMap.xs,
      label: labelStyles
    },
    sm: {
      container: containerSizeStyleMap.sm,
      label: labelStyles
    },
    md: {
      container: containerSizeStyleMap.md,
      label: labelStyles
    },
    lg: {
      container: containerSizeStyleMap.lg,
      label: labelStyles
    },
    xl: {
      container: containerSizeStyleMap.xl,
      label: labelStyles
    },
    '2xl': {
      container: containerSizeStyleMap['2xl'],
      label: labelStyles
    },
    '3xl': {
      container: containerSizeStyleMap['3xl'],
      label: labelStyles
    },
    '4xl': {
      container: containerSizeStyleMap['4xl'],
      label: labelStyles
    }
  }
};

export default AvatarTheme;
